import { MouseEvent, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";

import {
  getDatasetTags,
  getTempExportCount,
  getExportRequestData,
  getTemporaryAssignedTags,
} from "redux/SingleDataset/selectors";
import {
  saveTemporarySelection,
  setTemporaryExportData,
  submitRemoveTagsRequest,
  fetchTemporaryAssignedTags,
} from "redux/SingleDataset/actions";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

import { ERROR_RED, PARROT_GREEN } from "helpers/constants/colors";

import AssignTagsToast from "views/components/Cart/AssignTagsToast";
import FloatingPanel from "views/components/FloatingPanel";

import { useTagAssigment } from "../Cart/TagsDropDown/useTagAssigment";

type ToastActionType = "assign" | "remove";

const FloatingActions = () => {
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);

  const tagDropdownBtnRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<any>(null);

  const queryParamsFilters = useAllQueryParamsFilters();
  const { datasetId, clusterId } = useParams();
  const dispatch = useDispatch();

  const exportCount = useSelector(getTempExportCount);
  const originalTags = useSelector(getDatasetTags);
  const assignedTags = useSelector(getTemporaryAssignedTags);
  const exportRequestData = useSelector((state) =>
    getExportRequestData(state, queryParamsFilters, true),
  );

  const selectedTags = useMemo(
    () => assignedTags.filter((t) => t.selected),
    [assignedTags],
  );

  const toastStyle = { borderRadius: "10px", marginBottom: "10px" };
  const { handleTagSubmit } = useTagAssigment(true); // tempExport = true

  const handleAssignTagsClick = () => {
    setShowTagsDropdown(!showTagsDropdown);
  };

  const toggleShowTagsDropdown = (e: MouseEvent) => {
    e.stopPropagation();
    setShowTagsDropdown(!showTagsDropdown);
  };

  const successNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? `Tag successfully assigned to ${exportCount}  ${
            exportCount > 1 ? "items" : "item"
          } `
        : `Tag successfully de-assigned from ${exportCount} ${
            exportCount > 1 ? "items" : "item"
          }  `;
    toastIdRef.current = toast(
      <AssignTagsToast
        heading={`Tag assignment`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,

      {
        style: { ...toastStyle, border: `2px solid ${PARROT_GREEN}` },
        closeButton: false,
      },
    );
  };

  const errorNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? "Error occoured while tagging. Please try again"
        : "Error occoured while removing tags. Please try again";
    toastIdRef.current = toast(
      <AssignTagsToast
        heading={`Error!`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      {
        closeOnClick: false,
        closeButton: false,
        style: { ...toastStyle, border: `2px solid ${ERROR_RED}` },
      },
    );
  };

  const handleSaveSelection = () => {
    dispatch(saveTemporarySelection(datasetId!) as any);
  };

  const handleTagSubmission = async (tagIDs: string[]) => {
    const response = await handleTagSubmit(tagIDs);
    if (response.success) {
      successNotification("assign");
      dispatch(setTemporaryExportData(null));
    } else {
      dispatch(
        fetchTemporaryAssignedTags(datasetId!, exportRequestData) as any,
      );
      toast.dismiss(toastIdRef.current);
      errorNotification("assign");
    }
  };

  const handleTagRemoval = async (tagID: string) => {
    try {
      let requestBody: any = { [tagID]: exportRequestData };
      await dispatch(
        submitRemoveTagsRequest(
          datasetId!,
          requestBody,
          queryParamsFilters,
          clusterId,
        ) as any,
      );
      successNotification("remove");
    } catch (error) {
      toast.dismiss(toastIdRef.current);
      errorNotification("remove");
    }
  };

  return (
    <FloatingPanel
      type="actions"
      showTagsDropdown={showTagsDropdown}
      tagDropdownBtnRef={tagDropdownBtnRef}
      allTags={originalTags}
      datasetID={datasetId!}
      assignedTags={selectedTags}
      toggleShowTags={toggleShowTagsDropdown}
      onTagSubmission={handleTagSubmission}
      onTagRemoval={handleTagRemoval}
      onAssignTagsClick={handleAssignTagsClick}
      onSaveSelection={handleSaveSelection}
    />
  );
};
export default FloatingActions;
